import { useRef, useEffect, Fragment } from "react";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import { useSnackbar } from 'notistack';
import PropTypes from 'prop-types';

const Potree = window.Potree;

/**
 * Base Potree Viewer Component
 * Shared logic between PotreeViewer and PublicPotreeViewer
 */
function BasePotreeViewer({ 
  filename, 
  description, 
  metadata, 
  hierarchy, 
  octree,
  showTranslations = true,
  onError,
  onSuccess,
  children
}) {
  const { t } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();
  const potreeRenderArea = useRef();
  const navigate = useNavigate();

  useEffect(() => {
    let viewer;
    let mounted = true;

    const initViewer = async () => {
      try {
        // Wait for DOM element to be available
        let attempts = 0;
        const maxAttempts = 20;
        
        while (!potreeRenderArea.current && attempts < maxAttempts && mounted) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }
        
        if (!potreeRenderArea.current) {
          throw new Error('Potree render area not available');
        }

        // Wait for element to have dimensions
        attempts = 0;
        while ((potreeRenderArea.current.clientWidth === 0 || potreeRenderArea.current.clientHeight === 0) && attempts < maxAttempts && mounted) {
          await new Promise(resolve => setTimeout(resolve, 100));
          attempts++;
        }

        viewer = new Potree.Viewer(potreeRenderArea.current);

        viewer.setEDLEnabled(true);
        viewer.setFOV(50);
        viewer.setPointBudget(1_000_000);
        viewer.loadSettingsFromURL();
        viewer.setBackground("gradient"); 
        viewer.setDescription(description);

        viewer.loadGUI(() => {
          if (showTranslations && localStorage.getItem('languageMirukuru') !== null) {          
            viewer.setLanguage(localStorage.getItem('languageMirukuru'));
          } else {
            viewer.setLanguage("en");
          }
        });

        Potree.loadPointCloud(
          metadata,
          hierarchy,
          octree,
          filename,
          (e) => {
            if (!mounted) return;
            
            let scene = viewer.scene;
            let pointcloud = e.pointcloud;
            let material = pointcloud.material;
            material.size = 1;
            material.pointSizeType = Potree.PointSizeType.ADAPTIVE;
            material.activeAttributeName = "elevation";
            material.shape = Potree.PointShape.SQUARE;
            scene.addPointCloud(pointcloud);

            viewer.fitToScreen();
            
            if (onSuccess) {
              onSuccess(viewer);
            }
          }
        );
      } catch (e) {
        console.error('Error initializing Potree viewer:', e);
        if (mounted) {
          const errorMessage = showTranslations ? t("ErrorPotree") : "Error loading Potree viewer";
          if (enqueueSnackbar) {
            enqueueSnackbar(errorMessage, { variant: 'warning' });
          }
          if (onError) {
            onError(e);
          }
          if (navigate) {
            navigate(-1);
          }
        }
      }
    };

    initViewer();

    return () => {
      mounted = false;
      if (viewer) {
        viewer.renderer.clear();
        viewer = null;

        // Clear the profile window elements
        const profileWindow = document.getElementById("profile_window");
        if (profileWindow) {
          profileWindow.parentNode.removeChild(profileWindow);
        }
      }
    };
  }, [description, filename, hierarchy, metadata, octree, enqueueSnackbar, navigate, t, showTranslations, onError, onSuccess]);

  return (
    <Fragment>
      {children}
      <div id="potree_render_area" ref={potreeRenderArea}></div>
      <div id="potree_sidebar_container"></div>
    </Fragment>
  );
}

BasePotreeViewer.propTypes = {
  filename: PropTypes.string.isRequired,
  description: PropTypes.string.isRequired,
  metadata: PropTypes.string.isRequired,
  hierarchy: PropTypes.string.isRequired,
  octree: PropTypes.string.isRequired,
  showTranslations: PropTypes.bool,
  onError: PropTypes.func,
  onSuccess: PropTypes.func,
  children: PropTypes.node
};

export default BasePotreeViewer;